import React, { useEffect, useState, useMemo } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { inventoryAPI } from '../../services';
import { toast } from 'react-toastify';

/**
 * ImportStockModal — admin nhập kho nhanh (không qua workflow duyệt).
 * Hỗ trợ chọn đơn vị nhập hàng (MaterialPackage) và tự động quy đổi về base unit.
 * materials prop cần được truyền từ parent đã fetch với include_packages=true.
 */
export default function ImportStockModal({ show, onHide, branchId, materials, suppliers, onSuccess }) {
  const [formData, setFormData] = useState({
    material_id: '',
    package_id: '',
    package_quantity: '',
    cost_per_unit: '',
    supplier_id: '',
    note: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData({
        material_id: '', package_id: '', package_quantity: '',
        cost_per_unit: '', supplier_id: '', note: '',
      });
    }
  }, [show]);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const selectedMaterial = useMemo(
    () => materials?.find((m) => m.id === formData.material_id),
    [materials, formData.material_id]
  );

  const materialPackages = selectedMaterial?.packages ?? [];

  const selectedPackage = useMemo(
    () => materialPackages.find((p) => p.id === formData.package_id),
    [materialPackages, formData.package_id]
  );

  // Tự động chọn package mặc định khi đổi NVL
  const handleMaterialChange = (materialId) => {
    const mat = materials?.find((m) => m.id === materialId);
    const pkgs = mat?.packages ?? [];
    const defaultPkg = pkgs.find((p) => p.is_default) ?? pkgs[0];
    handleChange('material_id', materialId);
    handleChange('package_id', defaultPkg?.id ?? '');
    handleChange('package_quantity', '');
  };

  // Tổng base unit = package.quantity_in_base × package_quantity
  const computedBaseQty = useMemo(() => {
    if (!selectedPackage || !formData.package_quantity) return 0;
    return Number(selectedPackage.quantity_in_base) * (parseFloat(formData.package_quantity) || 0);
  }, [selectedPackage, formData.package_quantity]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.material_id) { toast.error('Vui lòng chọn NVL'); return; }
    if (!formData.package_id) { toast.error('Vui lòng chọn đơn vị nhập hàng'); return; }
    if (!formData.package_quantity || parseFloat(formData.package_quantity) <= 0) {
      toast.error('Số lượng phải lớn hơn 0'); return;
    }
    if (computedBaseQty <= 0) { toast.error('Số lượng quy đổi phải lớn hơn 0'); return; }

    try {
      setLoading(true);
      await inventoryAPI.importStock({
        branch_id: branchId,
        material_id: formData.material_id,
        quantity: computedBaseQty,           // gửi base unit đã quy đổi
        unit_price: formData.cost_per_unit ? Number(formData.cost_per_unit) : undefined,
        supplier_id: formData.supplier_id || undefined,
        note: formData.note.trim() || undefined,
      });
      toast.success('Đã nhập kho thành công');
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err?.message || 'Không thể nhập kho');
    } finally {
      setLoading(false);
    }
  };

  const unitLabel = selectedPackage?.unit_label ?? selectedPackage?.name ?? '';

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Nhập kho nhanh</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* NVL */}
          <Form.Group className="mb-3">
            <Form.Label>Nguyên vật liệu <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={formData.material_id}
              onChange={(e) => handleMaterialChange(e.target.value)}
              required
            >
              <option value="">-- Chọn NVL --</option>
              {(materials || []).filter((m) => m.is_active !== false).map((m) => (
                <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Đơn vị nhập hàng */}
          {formData.material_id && (
            <Form.Group className="mb-3">
              <Form.Label>Đơn vị nhập hàng <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.package_id}
                onChange={(e) => handleChange('package_id', e.target.value)}
                required
              >
                <option value="">-- Chọn đơn vị --</option>
                {materialPackages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.unit_label ?? p.name}
                    {p.is_base_unit
                      ? ' (đơn vị cơ sở)'
                      : ` (= ${Number(p.quantity_in_base).toLocaleString('vi-VN')} ${selectedMaterial?.unit})`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Số lượng */}
          {formData.package_id && (
            <Form.Group className="mb-3">
              <Form.Label>
                Số lượng {unitLabel ? `(${unitLabel})` : ''} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                min="0.001"
                step="0.001"
                value={formData.package_quantity}
                onChange={(e) => handleChange('package_quantity', e.target.value)}
                required
              />
              {computedBaseQty > 0 && (
                <Form.Text className="text-muted">
                  = {computedBaseQty.toLocaleString('vi-VN')} {selectedMaterial?.unit}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {/* Đơn giá */}
          <Form.Group className="mb-3">
            <Form.Label>Đơn giá / {unitLabel || 'đơn vị'} (VNĐ)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={formData.cost_per_unit}
              onChange={(e) => handleChange('cost_per_unit', e.target.value)}
            />
          </Form.Group>

          {/* Nhà cung cấp */}
          <Form.Group className="mb-3">
            <Form.Label>Nhà cung cấp</Form.Label>
            <Form.Select
              value={formData.supplier_id}
              onChange={(e) => handleChange('supplier_id', e.target.value)}
            >
              <option value="">-- Không có --</option>
              {(suppliers || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Ghi chú */}
          <Form.Group className="mb-3">
            <Form.Label>Ghi chú</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.note}
              onChange={(e) => handleChange('note', e.target.value)}
              placeholder="Ghi chú nhập kho..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>Hủy</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Đang xử lý...' : 'Nhập kho'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
